<template>
  <div v-if="hasContent" class="answer-box" :class="variantClass">
    <div class="d-flex align-items-start">
      <font-awesome-icon :icon="['fal', iconName]" class="mr-2 mt-1" />
      <div class="flex-grow-1 small">
        <div class="text-muted mb-2">{{ displayTitle }}</div>
        <div class="font-weight-bold">
          <div v-if="scaleText" class="mb-2">
            <ScaleIndicator :value="scaleValue" :max-value="5" />
          </div>
          <div v-if="answers && answers.length > 0" class="mb-1">
            <div v-for="(answer, index) in answers" :key="index" class="mb-1">
              <font-awesome-icon :icon="['far', 'dot-circle']" class="mr-1" size="sm" />
              <span>{{ answer.SurveyAnswerText }}</span>
            </div>
          </div>
        </div>
        <div v-if="commentText" class="font-italic text-muted mt-2">"{{ commentText }}"</div>
      </div>
    </div>
  </div>
</template>

<script>
import ScaleIndicator from "./ScaleIndicator.vue";

export default {
  name: "QuestionResult",

  components: {
    ScaleIndicator,
  },

  props: {
    /** Numeric result (0–1 range) for scale display. Omit or pass null to hide scale. */
    result: {
      type: Number,
      default: undefined,
    },
    /** Array of answer objects with SurveyAnswerText property. */
    selectedAnswers: {
      type: Array,
      default: null,
    },
    /** Comment text to display. */
    comment: {
      type: String,
      default: null,
    },
    /** Custom title text shown above the content. */
    title: {
      type: String,
      default: null,
    },
    /** Visual variant: 'default' (dark border) or 'success' (green border). */
    variant: {
      type: String,
      default: "default",
      validator: (v) => ["default", "success"].includes(v),
    },
  },

  computed: {
    effectiveResult() {
      if (this.result !== undefined && this.result !== null) return this.result;
      return null;
    },

    answers() {
      return this.selectedAnswers || [];
    },

    commentText() {
      return this.comment || null;
    },

    displayTitle() {
      return this.title || this.$t('Webportal.Actions.InquiryWizard.QuestionResult.DefaultTitle');
    },

    iconName() {
      return this.variant === "success" ? "reply" : "comment";
    },

    variantClass() {
      return this.variant === "success" ? "answer-box--success" : "";
    },

    hasContent() {
      return this.scaleText || (this.answers && this.answers.length > 0) || this.commentText;
    },

    scaleText() {
      if (this.effectiveResult !== null) {
        const scaleValue = Math.round(this.effectiveResult * 5);
        return `${scaleValue}/5`;
      }
      return null;
    },

    scaleValue() {
      if (this.effectiveResult !== null) {
        return Math.round(this.effectiveResult * 5);
      }
      return 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.answer-box {
  background-color: var(--light);
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
  border-left: 3px solid var(--dark);

  &--success {
    background-color: var(--success-55);
    border-left-color: var(--success);
  }
}
</style>
